import { useState, useCallback } from 'react';
import type { FormInstance } from 'antd';
import { APP_LANGUAGES, DEFAULT_LANGUAGE, REQUIRED_LANGUAGE, type LanguageCode } from '@/shared/config/languages';
import type { TranslationValue } from '@/shared/types';

export const useMultiLang = (initialLang: LanguageCode = DEFAULT_LANGUAGE) => {
  const [activeLang, setActiveLang] = useState<LanguageCode>(initialLang);

  const handleLangChange = useCallback((lang: LanguageCode) => {
    setActiveLang(lang);
  }, []);

  const resetLang = useCallback(() => {
    setActiveLang(DEFAULT_LANGUAGE);
  }, []);

  const goToRequiredLang = useCallback(
    (errorInfo?: { errorFields?: { name: (string | number)[] }[] }) => {
      if (errorInfo?.errorFields?.length) {
        for (const lang of REQUIRED_LANGUAGE) {
          const hasError = errorInfo.errorFields.some((field) => field.name.includes(lang));
          if (hasError) {
            setActiveLang(lang);
            return;
          }
        }
      }
      setActiveLang(REQUIRED_LANGUAGE[0]);
    },
    []
  );

  const validateAndNormalize = useCallback(
    async <T>(
      form: FormInstance<T>,
      translationFieldNames: string[]
    ): Promise<{ isValid: boolean; values: T | null }> => {
      try {
        await form.validateFields();

        const values = form.getFieldsValue(true);

        translationFieldNames.forEach((fieldPath) => {
          const pathParts = fieldPath.split('.');
          let current: Record<string, unknown> = values;

          for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!current[part]) {
              current[part] = {};
            }
            current = current[part] as Record<string, unknown>;
          }

          const lastPart = pathParts[pathParts.length - 1];
          const translationObj = (current[lastPart] || {}) as TranslationValue;

          const normalizedTranslation: TranslationValue = {};
          APP_LANGUAGES.forEach((lang) => {
            normalizedTranslation[lang.code] = translationObj[lang.code] ?? '';
          });

          current[lastPart] = normalizedTranslation;
        });

        return { isValid: true, values: values as T };
      } catch (error) {
        goToRequiredLang(error as { errorFields?: { name: (string | number)[] }[] });
        return { isValid: false, values: null };
      }
    },
    [goToRequiredLang]
  );

  return {
    activeLang,
    handleLangChange,
    resetLang,
    goToRequiredLang,
    validateAndNormalize,
  };
};
